import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2, Upload, Loader2, ChevronLeft, ChevronRight, Plus, Minus } from 'lucide-react';
import { BACKEND_URL } from './backendConfig';
import useEscapeClose from './useEscapeClose';
import i18n from './i18n';

const API = `${BACKEND_URL}/api/development/photos`;
/* Image <img src> must be RELATIVE, not BACKEND_URL.
   A plain <img> never sends cookies to another origin, and on production the API lives on
   helinoxvn.com:5000 while the page is on helinoxvn.com:80 — a different origin because the port
   differs. So the picture request arrived without the auth cookie, got 401, and every thumbnail
   rendered broken. It worked on the test server only because BACKEND_URL is '' there, making the
   call same-origin by accident.
   A relative path is same-origin on both, and nginx proxies /api on :80 and :3000 alike. fetch()
   keeps using BACKEND_URL: those calls pass credentials explicitly and CORS already allows them. */
const IMG = '/api/development/photos';
/* There is no ceiling on how many photos one project or Base ID carries (Max, 2026-09-11: "bỏ giới
   hạn tối đa 10 hình"). Ten was never a product rule — it was the number picked when the store was
   new — and projects had started sitting exactly on it. The server still refuses past 400, but that
   is a guard against a runaway client filling the disk, not a quota anybody works against, so it is
   not counted towards on screen. */
const ACCEPT = 'image/jpeg,image/png';
const MAX_EDGE = 1600;      // long edge after resize
const QUALITY = 0.82;       // JPEG quality — 0.82 keeps stitching and print detail readable
// Mirrors PROJECT_PHOTO_MAX_BYTES on the server. Kept here so the note the user reads and the rule
// that rejects the upload come from the same number; if the server value changes, change both.
const MAX_MB = 3;
// One compact line: the facts Max asked to state, without the prose he then asked to drop
// (2026-08-19). HEIC still gets named here because it is the iPhone default and the only format
// people routinely try that cannot work at all. The "up to N per project" clause left with the cap.
const limitsNote = (t) => t('xPhotos.limits', { px: MAX_EDGE, mb: MAX_MB });

/* Resize and re-encode in the browser before upload.
   Why here and not on the server: a phone photo is 3-5 MB and would have to cross the network in
   full, and re-encoding server-side would mean a native image library (sharp) that breaks on every
   npm rebuild. A canvas does it in the page, for free.
   PNG is kept as PNG — illustrations and screenshots go blurry as JPEG — but is still resized. */
export const compressImage = (file) => compress(file);
const compress = (file) => new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
        URL.revokeObjectURL(url);
        const scale = Math.min(1, MAX_EDGE / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, w, h);
        const keepPng = file.type === 'image/png';
        canvas.toBlob((blob) => {
            // Messages read in the reader's language at the moment they are raised — they are shown as is.
            if (!blob) { reject(new Error(i18n.t('xPhotos.cantProcess'))); return; }
            const reader = new FileReader();
            reader.onload = () => resolve({
                mimeType: keepPng ? 'image/png' : 'image/jpeg',
                dataBase64: String(reader.result).split(',')[1],
            });
            reader.onerror = () => reject(new Error(i18n.t('xPhotos.cantRead')));
            reader.readAsDataURL(blob);
        }, keepPng ? 'image/png' : 'image/jpeg', keepPng ? undefined : QUALITY);
    };
    // HEIC (the iPhone default) lands here: browsers cannot decode it, so say so plainly instead of
    // letting it fail as "something went wrong".
    img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error(i18n.t('xPhotos.heic', { name: file.name })));
    };
    img.src = url;
});

/* Full-size viewer. Kept as its own component so the arrow keys can be bound while it is open and
   unbound when it closes — Max asked to page through the photos instead of closing and re-opening
   one at a time (2026-08-19).

   ZOOM — Max, 2026-09-14. These are technical drawings: "Front / Back / Bottom / Waist Belt" on one
   sheet at 121 KB, where the thing being checked is a 6pt label on a strap. Fitting the sheet to the
   screen is what the viewer was for; reading it needs magnification.

   Wheel, the +/- buttons, double-click and the + - 0 keys all drive one number. Above 1x the image
   can be dragged, and a drag must NOT close the viewer — the backdrop closes on click, and a mouse
   that went down on the image and up somewhere else still fires one. `moved` is what tells the two
   apart. Zoom resets when the photo changes: arriving at the next drawing already magnified into a
   corner of it is disorienting, and nobody asked for it. */
function Lightbox({ items, at, onMove, onClose }) {
    const { t } = useTranslation();
    const photo = items[at];
    const [scale, setScale] = useState(1);
    const [pan, setPan] = useState({ x: 0, y: 0 });
    const drag = useRef(null);
    const moved = useRef(false);

    const MIN = 1;
    const MAX = 6;
    const clamp = (n) => Math.min(MAX, Math.max(MIN, n));
    const zoomTo = useCallback((next) => {
        const s = clamp(next);
        setScale(s);
        // Back to fit means back to centre; leaving the pan behind would show an off-centre photo
        // that cannot be dragged, because dragging is only offered above 1x.
        if (s === 1) setPan({ x: 0, y: 0 });
    }, []);

    const go = (delta) => onMove((at + delta + items.length) % items.length);   // wraps both ways
    // Esc goes through the shared stack so it shuts the viewer WITHOUT also shutting the project
    // form underneath — the arrows stay local, nothing else listens for them.
    useEscapeClose(onClose);
    useEffect(() => { setScale(1); setPan({ x: 0, y: 0 }); }, [at]);
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'ArrowRight') onMove((at + 1) % items.length);
            if (e.key === 'ArrowLeft') onMove((at - 1 + items.length) % items.length);
            if (e.key === '+' || e.key === '=') zoomTo(scale + 0.5);
            if (e.key === '-' || e.key === '_') zoomTo(scale - 0.5);
            if (e.key === '0') zoomTo(1);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [at, items.length, onMove, scale, zoomTo]);
    if (!photo) return null;
    const many = items.length > 1;

    /* The wheel is the one people reach for first. Non-passive, because the page behind would
        otherwise scroll while the photo zooms. */
    const onWheel = (e) => {
        e.preventDefault();
        zoomTo(scale + (e.deltaY < 0 ? 0.35 : -0.35));
    };
    const onDown = (e) => {
        if (scale <= 1) return;
        moved.current = false;
        drag.current = { x: e.clientX - pan.x, y: e.clientY - pan.y };
    };
    const onMoveMouse = (e) => {
        if (!drag.current) return;
        moved.current = true;
        setPan({ x: e.clientX - drag.current.x, y: e.clientY - drag.current.y });
    };
    const endDrag = () => { drag.current = null; };

    return (
        // The backdrop closes on click, so every control on top of it has to stop the event or the
        // viewer would shut the moment you tried to page forward.
        <div className="pp-lightbox" role="dialog" aria-modal="true" onClick={onClose}>
            <button type="button" className="pp-lb-close" title={t('xPhotos.close')} onClick={(e) => { e.stopPropagation(); onClose(); }}>&times;</button>
            {many && (
                <button type="button" className="pp-lb-nav prev" title={t('xPhotos.prev')} onClick={(e) => { e.stopPropagation(); go(-1); }}>
                    <ChevronLeft size={26} />
                </button>
            )}
            <div
                className={`pp-lb-stage ${scale > 1 ? 'zoomed' : ''}`}
                onWheel={onWheel}
                onMouseDown={onDown}
                onMouseMove={onMoveMouse}
                onMouseUp={endDrag}
                onMouseLeave={endDrag}
                /* A drag that ended here is not a click on the backdrop. */
                onClick={(e) => { e.stopPropagation(); if (moved.current) moved.current = false; }}
                onDoubleClick={(e) => { e.stopPropagation(); zoomTo(scale > 1 ? 1 : 2.5); }}
            >
                <img
                    src={photo.src}
                    alt={photo.originalName || t('xPhotos.altSample')}
                    draggable={false}
                    style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${scale})` }}
                />
            </div>
            {many && (
                <button type="button" className="pp-lb-nav next" title={t('xPhotos.next')} onClick={(e) => { e.stopPropagation(); go(1); }}>
                    <ChevronRight size={26} />
                </button>
            )}
            <div className="pp-lb-zoom" onClick={(e) => e.stopPropagation()}>
                <button type="button" title={t('xPhotos.zoomOut')} disabled={scale <= MIN} onClick={() => zoomTo(scale - 0.5)}>
                    <Minus size={15} />
                </button>
                {/* The number is the button that undoes everything — the one control people hunt for
                    after they have zoomed into the wrong corner. */}
                <button type="button" className="pp-lb-pct" title={t('xPhotos.fit')} onClick={() => zoomTo(1)}>
                    {Math.round(scale * 100)}%
                </button>
                <button type="button" title={t('xPhotos.zoomIn')} disabled={scale >= MAX} onClick={() => zoomTo(scale + 0.5)}>
                    <Plus size={15} />
                </button>
            </div>
            <div className="pp-lightbox-cap" onClick={(e) => e.stopPropagation()}>
                {many && <strong>{at + 1} / {items.length}</strong>}
                {many ? ' · ' : ''}{photo.originalName}
                {photo.bytes ? ` · ${Math.round(photo.bytes / 1024)} KB` : ''}
                {photo.uploadedBy ? ` · ${photo.uploadedBy}` : ''}
            </div>
        </div>
    );
}

/* Compress a list of files, reporting each failure without abandoning the rest. */
const compressAll = async (files) => {
    const out = []; const errors = [];
    for (const file of files) {
        try {
            // eslint-disable-next-line no-await-in-loop
            const payload = await compress(file);
            out.push({ ...payload, fileName: file.name, localId: `${file.name}-${file.size}-${out.length}` });
        } catch (err) { errors.push(err.message); }
    }
    return { out, errors };
};

/* Sample photos for one project.

   Two modes, because Max wants photos attached while the project is being CREATED as well as
   afterwards (2026-08-19), and a project has no PRJ-ID until it is saved:
     - with a groupId: every action hits the API immediately;
     - `pending` (no groupId): the compressed images are held in the page and handed to the parent,
       which uploads them once the project exists. Nothing is written if the create fails, so there
       are no orphan files and no temporary-upload area to clean up.
   Read-only for a viewer, editable for anyone who may edit the project — the endpoints enforce the
   same split, this only decides what is drawn. */
export default function ProjectPhotos({
    groupId, scope = 'project', ownerKey, readOnly = false,
    pending = false, pendingPhotos = [], onPendingChange, onChange, hideTitle = false,
    rollupGroupId = '',
    /* A CAPTION, FOR THE SCOPES THAT HAVE ONE — GTM, 2026-10-07. Gated on a prop rather than on
       `scope === 'gtm'`, because the six scopes that have never had a caption should not have
       their tiles grow a box the day a seventh wants one; and the column is NULL for all of them,
       so a caption drawn there would be an empty field under every photograph in the portal.
       `captionOf` / `onCaption` keep the TEXT in the caller: the caption is typed while the sheet
       is unlocked and saved with everything else, so this component holds no draft of its own. */
    captions = false, captionOf, onCaption,
    /* THE DOOR, for a caller that cannot use /api/development/photos — the partner track (2026-10-18):
       a partner's session is refused everything outside its own track, so its Base ID photos live
       behind /api/partner-samples/photos with the same shape. */
    apiPath = '',
    /* A STRIP OF THUMBNAILS AND NOTHING ELSE — Pricing Approval's quote panel, 2026-10-20 (Max: "thêm hình
       đã đính kèm vào khi mở phiếu pricing trong approval nhé"). The approver reads the quote there, and the
       photos lived only on the calculator. Read-only by construction (no add, no delete, no limits line),
       and it draws NOTHING when the quote has no photo: a "No photo yet" in a sticky head would be a line
       on every quote saying something nobody asked. A click opens the same viewer as everywhere else. */
    compact = false,
    /* At most this many tiles in the compact strip; the last one carries "+N" for the rest and opens the
       viewer there, so a quote with nine photos does not push the head down a row. 0 = all. */
    max = 0,
}) {
    const { t } = useTranslation();
    const api = apiPath ? `${BACKEND_URL}${apiPath}` : API;
    const img = apiPath || IMG;
    /* THE GROUP'S BAND IS A ROLL-UP — Max, 2026-09-21: upload moved down to the SKU, and this strip
       stays as "chỗ để tổng hợp ảnh của SKU để nhìn nhanh chứ ko cần mở từng SKU lên để xem".
       It is READ-ONLY by construction and not by a prop: there is no one owner to add a photo to,
       so an Add button here would have to ask which item first, which is the question the SKU's own
       box already answers by where it sits. */
    const rollup = !!rollupGroupId;
    // One store, two kinds of owner: a PRJ group or a Base ID. `groupId` stays accepted because the
    // project screens pass it, and it means the same thing as ownerKey for scope 'project'.
    const key = ownerKey || groupId;
    const title = (scope === 'opa' || scope === 'cpq') ? t('xPhotos.photos') : t('xPhotos.samplePhotos');
    const [photos, setPhotos] = useState([]);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [zoomAt, setZoomAt] = useState(-1);   // index in `shown`, -1 = closed
    const [askDelete, setAskDelete] = useState(null);   // the photo waiting on a confirmation
    const [roll, setRoll] = useState({ filtered: false, hidden: 0 });
    const fileRef = useRef(null);

    const load = useCallback(async () => {
        if (!rollup && !key) return;
        try {
            const url = rollup
                ? `${api}/group-rollup/${encodeURIComponent(rollupGroupId)}`
                : `${api}/${scope}/${encodeURIComponent(key)}`;
            const res = await fetch(url, { credentials: 'include' });
            const data = await res.json();
            if (res.ok) {
                setPhotos(data.photos || []);
                if (rollup) setRoll({ filtered: !!data.filtered, hidden: data.hiddenSkus || 0 });
            }
        } catch (e) { /* a failed list is not worth a banner; the empty state reads the same */ }
    }, [scope, key, rollup, rollupGroupId, api]);
    useEffect(() => { load(); }, [load]);
    /* A caller that shows a count of its own — the Collab Pricing band folds this strip away and
       puts the number in its header — needs to hear about every add and delete, not just the first
       read. Reported from the state itself so there is no third place counting photos. */
    useEffect(() => { if (onChange) onChange(photos); }, [photos, onChange]);

    const pick = async (event) => {
        const files = Array.from(event.target.files || []);
        if (fileRef.current) fileRef.current.value = '';   // so picking the same file twice still fires
        if (!files.length) return;

        if (pending) {
            setBusy(true); setError('');
            const { out, errors } = await compressAll(files);
            onPendingChange([...pendingPhotos, ...out]);
            setError(errors.filter(Boolean).join(' '));
            setBusy(false);
            return;
        }

        setBusy(true); setError('');
        let added = 0;
        let stopped = '';
        for (const file of files) {
            try {
                // eslint-disable-next-line no-await-in-loop
                const payload = await compress(file);
                // eslint-disable-next-line no-await-in-loop
                const res = await fetch(`${api}/${scope}/${encodeURIComponent(key)}`, {
                    method: 'POST', credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ ...payload, fileName: file.name }),
                });
                // eslint-disable-next-line no-await-in-loop
                const data = await res.json();
                if (!res.ok) throw new Error(data.error || t('xPhotos.uploadFailed', { status: res.status }));
                setPhotos(data.photos || []);
                added += 1;
            } catch (err) {
                stopped = err.message;
                break;      // stop on the first failure rather than firing the rest at a broken path
            }
        }
        /* Said here and not from inside the loop, because `error` in that closure is the value this
           render started with — reading it back would have overwritten the reason with the count. */
        if (stopped) setError(t('xPhotos.partialUpload', { reason: stopped, added, total: files.length }));
        setBusy(false);
    };

    /* The portal's own dialog, not window.confirm (Max, 2026-08-29 — the same call he made on the
       pricing calculator and the master sheet). A native alert reads as the page having gone wrong,
       and it cannot name the photo in the page's own voice. The question lives in `askDelete`; this
       only ever runs once somebody has answered it. */
    const remove = async (photo) => {
        setAskDelete(null);
        setBusy(true); setError('');
        try {
            const res = await fetch(`${api}/file/${photo.photoId}`, { method: 'DELETE', credentials: 'include' });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || t('xPhotos.deleteFailed'));
            /* the DELETE answers with the photos of the one owner it deleted from; a roll-up shows several owners, so
               it reads the whole list again rather than replacing it with one owner's */
            if (rollup) await load(); else setPhotos(data.photos || []);
        } catch (err) { setError(err.message); }
        setBusy(false);
    };

    if (!key && !pending && !rollup) return null;
    const shown = pending
        ? pendingPhotos.map((p) => ({ ...p, key: p.localId, src: `data:${p.mimeType};base64,${p.dataBase64}`, originalName: p.fileName }))
        : photos.map((p) => ({ ...p, key: p.photoId, src: `${img}/file/${p.photoId}` }));
    if (compact) {
        if (!shown.length) return null;
        const tiles = max > 0 ? shown.slice(0, max) : shown;
        const more = shown.length - tiles.length;
        return (
            <div className="pp-compact" data-testid="pp-compact">
                {tiles.map((p, idx) => {
                    const last = more > 0 && idx === tiles.length - 1;
                    return (
                        <button key={p.key} type="button" className="pp-cthumb" onClick={() => setZoomAt(idx)}
                            title={last ? t('xPhotos.morePhotos', { n: more + 1 }) : (p.originalName || t('xPhotos.view'))}>
                            <img src={p.src} alt={p.originalName || t('xPhotos.altPhoto')} loading="lazy" />
                            {last && <span className="pp-cmore">+{more + 1}</span>}
                        </button>
                    );
                })}
                {zoomAt >= 0 && shown[zoomAt] && (
                    <Lightbox items={shown} at={zoomAt} onMove={setZoomAt} onClose={() => setZoomAt(-1)} />
                )}
            </div>
        );
    }
    return (
        <div className="pp-wrap">
            <div className="pp-head">
                {/* A caller that already labels the strip — the Collab Pricing band puts "Photos"
                    and the count on its own folded header — would otherwise say it twice. */}
                {!hideTitle && <span className="pp-title">{title} <small>{shown.length}</small></span>}
                {!readOnly && !rollup && (
                    <>
                        <input
                            ref={fileRef} type="file" accept={ACCEPT} multiple
                            style={{ display: 'none' }} onChange={pick}
                        />
                        <button
                            type="button" className="btn btn-ghost btn-sm pp-add"
                            disabled={busy}
                            onClick={() => fileRef.current?.click()}
                        >
                            {busy ? <Loader2 size={14} className="pp-spin" /> : <Upload size={14} />}
                            {' '}{busy ? t('xPhotos.uploading') : t('xPhotos.addPhotos')}
                        </button>
                    </>
                )}
            </div>
            {/* The roll-up is not a place anything is uploaded, so the upload limits would be
                advice about a button that is not there. It says where the photos come from
                instead. */}
            <div className="pp-limits">
                {rollup
                    ? (roll.filtered
                        ? t('xPhotos.rollupFiltered', { n: roll.hidden })
                        : t('xPhotos.rollupAll'))
                    : limitsNote(t)}
            </div>
            {!!error && <div className="pp-error">{error}</div>}
            {!shown.length && <div className="pp-empty">{t('xPhotos.noPhoto')}</div>}
            {pending && !!shown.length && (
                <div className="pp-empty">{t('xPhotos.pendingNote')}</div>
            )}
            {/* ONE PHOTO PER ROW WHEN IT CARRIES A CAPTION — Max, 2026-10-08: "chỗ này nhỏ quá
                nên tôi nghĩ mỗi hình là 1 hàng và ghi chú hình nằm kế bên phải thì okie hơn". In
                the grid the caption box inherits the THUMBNAIL's width, about 100px, so "What
                this photo shows" was cut to three words and a caption of any length could not be
                read back. A row gives it the rest of the line. The grid is untouched for every
                scope that passes no caption — there the tile is the whole object and a column of
                one-per-row would waste the width. */}
            <div className={`pp-grid${captions && !rollup ? ' pp-rows' : ''}`}>
                {shown.map((p, idx) => (
                    <figure key={p.key} className="pp-item">
                        <button type="button" className="pp-thumb" onClick={() => setZoomAt(idx)} title={p.originalName || t('xPhotos.view')}>
                            <img src={p.src} alt={p.originalName || t('xPhotos.altSample')} loading="lazy" />
                        </button>
                        {/* WHICH ITEM IT IS OF. Nine pictures of three chairs is not a summary
                            of anything until each one says which chair. */}
                        {rollup && (
                            <figcaption className="pp-of" title={p.itemName || p.skuId || ''}>
                                {p.skuId ? `${p.skuId}${p.itemName ? ` · ${p.itemName}` : ''}` : t('xPhotos.projectTag')}
                            </figcaption>
                        )}
                        {/* THE WORDS THE PICTURE IS THERE FOR. The GTM masterplan's own two
                            photos are captioned "candidate spot for initials" and "clip position
                            to be confirmed by product team" — without them the pictures are two
                            chairs. Read-only it is a line of text and takes no room when empty;
                            unlocked it is the box, so the caption is edited where it is read. */}
                        {captions && !rollup && (readOnly
                            ? (!!captionOf?.(p) && <figcaption className="pp-cap">{captionOf(p)}</figcaption>)
                            : (
                                <input
                                    className="pp-cap-in" value={captionOf?.(p) || ''}
                                    placeholder={t('xPhotos.captionPh')}
                                    onChange={(e) => onCaption?.(p, e.target.value)}
                                />
                            ))}
                        {/* A SKU's photo is removed on the SKU; a photo uploaded on the PROJECT itself has no other
                            place to be removed from — Tina deleted two SKUs of PRJ-00163 and eight project-level
                            pictures stayed (Max, 23/10: "cho phép xóa hình mà trước upload trên project nhé"). */}
                        {!readOnly && (!rollup || !p.skuId) && (
                            <button
                                type="button" className="pp-del" title={t('xPhotos.removeTitle')} disabled={busy}
                                onClick={() => setAskDelete(p)}
                            >
                                <Trash2 size={12} />
                            </button>
                        )}
                    </figure>
                ))}
            </div>
            {!!askDelete && (
                <div className="pp-ask" role="dialog" aria-modal="true" onClick={() => setAskDelete(null)}>
                    <div className="pp-ask-box" onClick={(e) => e.stopPropagation()}>
                        <h4>{t('xPhotos.deleteAsk')}</h4>
                        <p>{t('xPhotos.deleteBody', { name: askDelete.originalName || askDelete.fileName || t('xPhotos.thisPhoto') })}</p>
                        <div className="pp-ask-btns">
                            <button type="button" className="pp-ask-cancel" onClick={() => setAskDelete(null)}>
                                {t('xPhotos.cancel')}
                            </button>
                            <button
                                type="button"
                                className="pp-ask-go"
                                autoFocus
                                onClick={() => {
                                    // A pending photo was never uploaded; it just leaves the list.
                                    if (pending) {
                                        onPendingChange(pendingPhotos.filter((x) => x.localId !== askDelete.localId));
                                        setAskDelete(null);
                                    } else remove(askDelete);
                                }}
                            >
                                <Trash2 size={13} /> {t('xPhotos.delete')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {zoomAt >= 0 && shown[zoomAt] && (
                <Lightbox
                    items={shown}
                    at={zoomAt}
                    onMove={setZoomAt}
                    onClose={() => setZoomAt(-1)}
                />
            )}
        </div>
    );
}

/* Upload photos held in the page to a project that now exists. Used by the New Project flow after
   the group id comes back. Returns how many landed, so the caller can say so plainly. */
export const uploadPendingPhotos = async (ownerKey, list = [], scope = 'project') => {
    let done = 0;
    for (const p of list) {
        try {
            // eslint-disable-next-line no-await-in-loop
            const res = await fetch(`${API}/${scope}/${encodeURIComponent(ownerKey)}`, {
                method: 'POST', credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ mimeType: p.mimeType, dataBase64: p.dataBase64, fileName: p.fileName }),
            });
            if (res.ok) done += 1;
        } catch (e) { /* the project is already created; a failed photo is reported, not fatal */ }
    }
    return done;
};
